import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/layout/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { columns, site } from '@/mocks/columns';

const tabs = [{ slug: 'all', name: '全部' }, ...columns.map((c) => ({ slug: c.slug, name: c.name }))];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '看料社全部栏目与文章',
    url: `${site.domain}/lanmu`,
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '看料社', url: site.domain },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: site.domain },
      { '@type': 'ListItem', position: 2, name: '看料栏目', item: `${site.domain}/lanmu` },
    ],
  },
];

export default function Columns() {
  const [active, setActive] = useState('all');
  const list = useMemo(
    () => (active === 'all' ? articles : articles.filter((a) => a.column === active)),
    [active],
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="看料栏目 · 深扒档案/脉络专栏/幕后再探/看料方法论 - 看料社"
        description="看料社四大看料栏目：深扒档案拆解事件纵深、脉络专栏梳理来龙去脉、幕后再探解读内容行业机制、看料方法论分享编辑部核查方法。汇集小编深扒、编辑扒皮、深度扒文与小编追踪内容。"
        keywords="看料社栏目,深扒档案,脉络专栏,幕后再探,看料方法论,小编深扒,专栏分析"
        canonical={`${site.domain}/lanmu`}
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="text-xs text-foreground-500" aria-label="面包屑">
              <ol className="flex flex-wrap items-center gap-1">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
                </li>
                <li aria-hidden="true" className="text-foreground-400">/</li>
                <li className="text-foreground-800" aria-current="page">看料栏目</li>
              </ol>
            </nav>
            <div className="mt-6 max-w-3xl">
              <p className="lede-mark text-xs font-semibold uppercase tracking-[0.22em] text-primary-600">
                Columns
              </p>
              <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                看料社全部栏目与文章
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                四大栏目覆盖不同层级的"看料"需求。选择栏目，或按上方标签快速筛选，找到你感兴趣的那一篇。
              </p>
            </div>
          </div>
        </section>

        {/* 栏目卡片 */}
        <section className="border-b border-background-200">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-12 md:grid-cols-2 md:px-6">
            {columns.map((c) => {
              const list = articles.filter((a) => a.column === c.slug);
              const head = list[0];
              return (
                <div
                  key={c.slug}
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="flex gap-5 p-5">
                    <div className="flex shrink-0 flex-col items-center justify-center rounded-md bg-background-100 px-4">
                      <span className="font-heading text-xl font-black uppercase text-primary-600">{c.mark}</span>
                      <span className="mt-1 text-[10px] uppercase tracking-widest text-secondary-600">{c.en}</span>
                    </div>
                    <div className="flex-1">
                      <h2 className="font-heading text-lg font-bold text-foreground-950">
                        {c.name}
                        <span className="ml-2 text-xs font-normal text-foreground-500">{list.length} 篇</span>
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{c.desc}</p>
                      <Link
                        to={`/lanmu/${c.slug}`}
                        className="mt-3 inline-flex items-center whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-800"
                      >
                        进入该栏目
                        <i className="ri-arrow-right-line ml-1 text-sm" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                  {head && (
                    <Link
                      to={`/wenzhang/${head.slug}`}
                      className="flex items-center gap-3 border-t border-background-200 bg-background-100/60 px-5 py-3 transition-colors hover:bg-background-100"
                    >
                      <img
                        src={head.cover}
                        alt={head.title}
                        title={`${head.title} - 看料社${c.name}`}
                        className="h-12 w-16 shrink-0 rounded object-cover object-top"
                      />
                      <div className="min-w-0">
                        <p className="text-[11px] text-secondary-700">栏目最近更新</p>
                        <p className="truncate text-sm font-medium text-foreground-900">{head.title}</p>
                      </div>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* 文章列表 + 筛选 */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                文章列表
                <span className="ml-2 text-sm font-normal text-foreground-500">{list.length} 篇</span>
              </h2>
            </div>

            <div className="mb-7 flex flex-wrap gap-2" role="tablist" aria-label="按栏目筛选">
              {tabs.map((t) => (
                <button
                  key={t.slug}
                  type="button"
                  role="tab"
                  aria-selected={active === t.slug}
                  onClick={() => setActive(t.slug)}
                  className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    active === t.slug
                      ? 'border-primary-600 bg-primary-600 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}